import { useEffect, useMemo, useState } from 'react';
import { formatRaidHpInput, truncateRaidHp } from '../../../shared/domain/raidHpFormat.js';
import {
  loadBossSeasonAssignmentCatalog,
  saveBossSeasonAssignment,
  setBossSeasonStatus,
} from '../api/bossSeasonAssignmentApi.js';
import {
  BOSS_SEASON_ELEMENTS,
  createBossSeasonAssignmentDraft,
  createNextBossSeasonDraft,
} from '../domain/bossSeasonAssignment.js';
import { createManualBossMaster } from '../api/bossMasterSyncApi.js';
import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { raidSeasonStatusLabel } from '../../raid-season/domain/raidSeasonPresentation.js';
import { bossMasterTriStateLabel } from '../domain/bossMasterCatalog.js';

function MasterFacts({ master }) {
  if (!master) return <p className="boss-season-master-empty">Boss Master를 선택해주세요.</p>;
  return (
    <div className="boss-season-master-facts">
      <span className="boss-season-master-image">
        {master.display_image_url ? <img src={master.display_image_url} alt="" /> : <b>NO IMAGE</b>}
      </span>
      <div>
        <strong>{master.canonical_name}</strong>
        <small>{master.icon_id} · {master.source_monster_model_id || 'model 미확인'}</small>
        <p>
          코어 {bossMasterTriStateLabel(master.has_core)} · 파츠 {bossMasterTriStateLabel(master.has_parts)}
          {master.has_parts === true && master.part_count !== null ? ` ${master.part_count}개` : ''}
        </p>
      </div>
      <em>Boss Master</em>
    </div>
  );
}

export function BossSeasonAssignmentPanel() {
  const [catalog, setCatalog] = useState({ masters: [], seasons: [] });
  const [selectedSeasonNo, setSelectedSeasonNo] = useState('');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [manualBossNo, setManualBossNo] = useState(null);
  const [manualName, setManualName] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = async (preferredSeasonNo = null) => {
    setLoading(true);
    setNotice({ type: '', message: '' });
    try {
      const next = await loadBossSeasonAssignmentCatalog();
      setCatalog(next);
      const requested = Number(preferredSeasonNo || selectedSeasonNo);
      const selected = next.seasons.find((season) => season.season_no === requested) || next.seasons[0] || null;
      setSelectedSeasonNo(selected ? String(selected.season_no) : '');
      setDraft(selected ? createBossSeasonAssignmentDraft(selected) : null);
      return true;
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '시즌 보스 배치를 불러오지 못했습니다.' });
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const mastersById = useMemo(
    () => new Map(catalog.masters.map((master) => [master.id, master])),
    [catalog.masters],
  );

  const selectSeason = (seasonNo) => {
    if (hasUnsavedChanges && draft && !window.confirm('저장하지 않은 변경을 버리고 차수를 바꿀까요?')) return;
    const season = catalog.seasons.find((item) => item.season_no === Number(seasonNo));
    if (!season) return;
    setSelectedSeasonNo(String(season.season_no));
    setDraft(createBossSeasonAssignmentDraft(season));
    setManualBossNo(null);
    setNotice({ type: '', message: '' });
  };

  const startNewSeason = () => {
    if (hasUnsavedChanges && draft && !window.confirm('저장하지 않은 변경을 버리고 새 시즌을 만들까요?')) return;
    const base = catalog.seasons.find((season) => season.season_no === Number(selectedSeasonNo));
    setSelectedSeasonNo('');
    setDraft(createNextBossSeasonDraft(catalog.seasons, base));
    setManualBossNo(null);
    setNotice({ type: 'success', message: base ? `${base.season_no}시즌의 보스·속성·체력을 복사했습니다.` : '새 시즌을 준비합니다.' });
  };

  const addManualBoss = async () => {
    if (saving || !manualName.trim()) return;
    setSaving(true);
    try {
      const existing = catalog.masters.find((master) => master.canonical_name.trim().toLocaleLowerCase() === manualName.trim().toLocaleLowerCase());
      const master = existing || await createManualBossMaster(manualName, draft.season_no);
      if (!existing) setCatalog((current) => ({ ...current, masters: [...current.masters, master] }));
      updateBoss(manualBossNo, 'boss_master_id', master.id);
      setManualBossNo(null);
      setManualName('');
      setNotice({ type: 'success', message: existing ? '등록된 같은 이름의 보스를 선택했습니다.' : '보스를 추가했습니다. 배치를 저장해주세요.' });
    } catch (error) { setNotice({ type: 'error', message: error.message }); }
    finally { setSaving(false); }
  };

  const updateBoss = (bossNo, field, value) => setDraft((current) => ({
    ...current,
    bosses: current.bosses.map((boss) => boss.boss_no === bossNo ? { ...boss, [field]: value } : boss),
  }));

  const save = async () => {
    if (!draft || saving) return;
    if (!draft.id && catalog.seasons.some((season) => season.season_no === Number(draft.season_no))) {
      setNotice({ type: 'error', message: '이미 등록된 시즌 번호입니다. 새 시즌 번호를 입력해주세요.' });
      return;
    }
    setSaving(true);
    setNotice({ type: '', message: '' });
    try {
      const saved = await saveBossSeasonAssignment(draft);
      if (!await refresh(saved?.season_no || draft.season_no)) return;
      setNotice({ type: 'success', message: `${saved?.season_no || draft.season_no}시즌 보스 배치를 저장했습니다.` });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '시즌 보스 배치를 저장하지 못했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const savedDraft = catalog.seasons.find((season) => season.id === draft?.id);
  const hasUnsavedChanges = !savedDraft || JSON.stringify(createBossSeasonAssignmentDraft(savedDraft)) !== JSON.stringify(draft);
  const changeStatus = async (status) => {
    if (!draft?.id || saving || hasUnsavedChanges) return;
    const message = status === 'active'
      ? `${draft.season_no}시즌을 모든 유니온에 공개할까요? 기존 활성 시즌은 종료됩니다.`
      : `${draft.season_no}시즌을 종료할까요? 새 수집은 중단되고 기존 기록은 유지됩니다.`;
    if (!window.confirm(message)) return;
    setSaving(true);
    try {
      await setBossSeasonStatus(draft.season_no, status);
      if (!await refresh(draft.season_no)) return;
      window.dispatchEvent(new CustomEvent('dororaid:raid-season-changed'));
      window.dispatchEvent(new CustomEvent('dororaid:raid-overview-changed'));
      setNotice({ type: 'success', message: `모든 유니온에 시즌 ${status === 'active' ? '공개' : '종료'}를 반영했습니다.` });
    } catch (error) {
      setNotice({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !draft) return <LoadingState label="시즌 배치 동기화 중" variant="boss" />;

  return (
    <section className="boss-season-assignment-panel" aria-label="시즌 보스 배치">
      <div className="boss-season-assignment-toolbar">
        <div>
          <strong>시즌별 보스 · 약점 · HP</strong>
          <small>새 시즌은 선택한 시즌의 보스·속성·1~3단 HP를 복사합니다.</small>
        </div>
        <label>
          <span>시즌</span>
          <select value={selectedSeasonNo} disabled={saving || !catalog.seasons.length} onChange={(event) => selectSeason(event.target.value)}>
            {!selectedSeasonNo && catalog.seasons.length ? <option value="">새 시즌 작성 중</option> : null}
            {!catalog.seasons.length ? <option value="">등록된 시즌 없음</option> : null}
            {catalog.seasons.map((season) => <option key={season.id} value={season.season_no}>{season.season_no}시즌 · {raidSeasonStatusLabel(season.status)}</option>)}
          </select>
        </label>
        <button type="button" disabled={saving} onClick={startNewSeason}>새 시즌</button>
      </div>

      {draft ? (
        <>
          <div className="boss-season-assignment-meta">
            <label><span>시즌 번호</span><input type="number" min="35" max="9999" value={draft.season_no} disabled={saving || Boolean(draft.id)} onChange={(event) => setDraft((current) => ({ ...current, season_no: event.target.value }))} /></label>
            <div><span>상태</span><strong>{raidSeasonStatusLabel(draft.status)}</strong><small>공개하면 모든 유니온에 적용됩니다.</small></div>
            <div><span>등록된 보스</span><strong>{catalog.masters.length}개</strong><small>목록에 없는 보스도 직접 추가할 수 있습니다.</small></div>
          </div>
          <div className="boss-season-assignment-list">
            {draft.bosses.map((boss) => {
              const master = mastersById.get(boss.boss_master_id);
              return (
                <article key={boss.boss_no} className="boss-season-assignment-card">
                  <header><span>{boss.boss_no}</span><strong>{master?.canonical_name || `${boss.boss_no}번 보스`}</strong><em>{boss.element}</em></header>
                  <label className="wide"><span>Boss Master</span><select value={boss.boss_master_id} disabled={saving} onChange={(event) => updateBoss(boss.boss_no, 'boss_master_id', event.target.value)}><option value="">선택</option>{catalog.masters.map((item) => <option key={item.id} value={item.id}>{item.canonical_name} · {item.icon_id}</option>)}</select></label>
                  <button type="button" disabled={saving} onClick={() => { setManualBossNo(boss.boss_no); setManualName(''); }}>목록에 없는 보스 추가</button>
                  {manualBossNo === boss.boss_no ? <div className="boss-season-manual-entry">
                    <label><span>새 보스 이름</span><input value={manualName} maxLength={80} disabled={saving} onChange={(event) => setManualName(event.target.value)} /></label>
                    <button type="button" disabled={saving || !manualName.trim()} onClick={addManualBoss}>추가 후 선택</button>
                    <button type="button" disabled={saving} onClick={() => setManualBossNo(null)}>취소</button>
                    <small>보스 목록에 등록됩니다. 이미지와 상세 정보는 보스 관리에서 수정할 수 있습니다.</small>
                  </div> : null}
                  <MasterFacts master={master} />
                  <div className="boss-season-assignment-fields">
                    {/* 빈 선택지가 없으면 속성이 비어 있는 보스도 첫 항목(작열)로 보인다. 그 상태로
                        저장하면 "약점 속성을 확인해주세요"만 뜨고 어느 보스인지 알 수 없었다. */}
                    <label><span>약점 속성</span><select value={boss.element} disabled={saving} onChange={(event) => updateBoss(boss.boss_no, 'element', event.target.value)}><option value="">선택</option>{BOSS_SEASON_ELEMENTS.map((element) => <option key={element}>{element}</option>)}</select></label>
                    {[1, 2, 3].map((phaseNo) => <label key={phaseNo}><span>{phaseNo}단 HP</span><div><input type="number" min="0" step="0.01" value={formatRaidHpInput(boss[`phase${phaseNo}_hp`])} disabled={saving} onChange={(event) => updateBoss(boss.boss_no, `phase${phaseNo}_hp`, event.target.value === '' ? '' : String(truncateRaidHp(event.target.value)))} /><small>억</small></div></label>)}
                  </div>
                </article>
              );
            })}
          </div>
          <div className="boss-season-assignment-actions">
            <span>{draft.status === 'draft' ? '준비 중인 시즌은 저장 후 공개해주세요.' : '저장하면 모든 유니온의 보스 · HP에 즉시 반영됩니다.'}</span>
            {hasUnsavedChanges ? <small>변경 내용을 먼저 저장해주세요.</small> : null}
            {draft.status !== 'active' ? <button type="button" disabled={saving || hasUnsavedChanges} onClick={() => changeStatus('active')}>전체 유니온에 공개</button> : <button type="button" disabled={saving || hasUnsavedChanges} onClick={() => changeStatus('closed')}>시즌 종료</button>}
            <button type="button" disabled={saving} onClick={save}>{saving ? '저장 중…' : '시즌 배치 저장'}</button>
          </div>
        </>
      ) : <p className="boss-master-catalog-empty">편집할 시즌을 선택하거나 새 시즌을 만들어주세요.</p>}
      {notice.message ? <p className={`boss-master-sync-notice ${notice.type}`} role="status">{notice.message}</p> : null}
    </section>
  );
}
